import { useEffect, useMemo, useRef, useState } from "react";
import { api, itemImage, publicItemImage, money } from "@/lib/api";
import { UserChip, timeAgo } from "@/components/Social";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeftRight, Tag, Scale, Loader2, Send, Ruler, Check, X, MessageCircle, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { toast } from "sonner";

const LISTING_LABEL = { none: "Not listed", sell: "For sale", swap: "Open to swap", both: "Sale or swap", sold: "Sold / traded" };
const SIZE_GROUP_LABEL = { tops: "Tops", bottoms: "Bottoms", shoes: "Shoes" };

/** "Your sizes: Tops L · XL  Bottoms 33" */
export const SizeChips = ({ sizes, compact = false }) => {
  if (!sizes) return null;
  const groups = Object.entries(sizes).filter(([, v]) => v?.length);
  if (groups.length === 0) return <span className="text-xs text-muted-foreground" data-testid="my-sizes-empty">Add sizes to your closet pieces to unlock size matching.</span>;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="my-sizes">
      {!compact && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Ruler className="h-3.5 w-3.5" /> Your sizes</span>}
      {groups.map(([g, list]) => (
        <span key={g} className="text-xs"><span className="text-muted-foreground">{SIZE_GROUP_LABEL[g] || g}</span> <b className="font-mono-plex">{list.slice(0, 3).join(" · ")}</b></span>
      ))}
    </div>
  );
};

export const FitBadge = ({ fits, size }) => {
  if (fits === true) return <span className="inline-flex items-center gap-1 rounded-full bg-good/25 text-foreground px-2 py-0.5 text-[10px] font-medium" data-testid="fit-badge-yes"><Check className="h-3 w-3" /> Fits you{size ? ` · ${size}` : ""}</span>;
  if (fits === false) return <span className="inline-flex items-center gap-1 rounded-full bg-background/90 border border-border px-2 py-0.5 text-[10px] text-muted-foreground" data-testid="fit-badge-no">{size || "Size"} · not yours</span>;
  return size ? <span className="rounded-full bg-background/90 border border-border px-2 py-0.5 text-[10px] text-muted-foreground" data-testid="fit-badge-unknown">{size}</span> : null;
};

const worthDelta = (a, b) => {
  if (a == null || b == null) return null;
  const d = b - a;
  const pct = a ? Math.round((d / a) * 100) : 0;
  return { d, pct };
};

/** Dialog: other people's swap-able pieces worth about the same as one of MY items, with "Propose trade". */
export const TradeMatchesDialog = ({ item, open, onOpenChange, onProposed }) => {
  const [fit, setFit] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [proposed, setProposed] = useState([]);
  useEffect(() => {
    if (!open || !item) return;
    setData(null);
    api.tradeMatches(item.id, fit).then(setData).catch((e) => { toast.error(e?.response?.data?.detail || "Could not load matches"); setData({ matches: [] }); });
  }, [open, item?.id, fit]); // eslint-disable-line react-hooks/exhaustive-deps

  const propose = async (m) => {
    setBusy(m.id);
    try {
      await api.makeOffer({ item_id: m.id, kind: "swap", offered_item_ids: [item.id], message: `Trade my ${item.name} for your ${m.name}?` });
      setProposed((p) => [...p, m.id]);
      toast.success(`Trade proposed to @${m.user?.handle}`);
      onProposed?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not propose"); }
    finally { setBusy(null); }
  };

  if (!item) return null;
  const w = data?.item?.worth ?? item.worth;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="trade-matches-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Scale className="h-5 w-5 text-action" /> Trade-worthy matches</DialogTitle>
          <DialogDescription>
            Pieces other people are open to swapping, worth about the same as your <b>{item.name}</b>
            {w != null && <> ({money(w)}{data?.window ? ` — looking between ${money(data.window[0])} and ${money(data.window[1])}` : ""})</>}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-3 rounded-xl bg-secondary/60 px-3 py-2">
          <div className="flex items-center gap-3">
            <img src={itemImage(item)} alt="" className="h-12 w-9 rounded-md object-cover bg-secondary" />
            <div><p className="text-sm font-medium">{item.name}</p><p className="text-xs text-muted-foreground">{item.size ? `${item.size} · ` : ""}worth {money(w)}</p></div>
          </div>
          <label className="flex items-center gap-2 text-xs"><span className="text-muted-foreground">Fits me</span><Switch checked={fit} onCheckedChange={setFit} data-testid="trade-matches-fit-switch" /></label>
        </div>
        {data === null ? <div className="grid gap-3 sm:grid-cols-2">{[0, 1].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
          : data.reason === "no_worth" ? <p className="text-sm text-muted-foreground" data-testid="trade-matches-noworth">This piece has no worth estimate yet — add a purchase price or run the retail estimate in your Closet.</p>
          : data.matches.length === 0 ? <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="trade-matches-empty">No fair matches right now{fit ? " in your size — try turning off “Fits me”" : ""}. Check back as more people list pieces.</div>
          : <div className="grid gap-3 sm:grid-cols-2" data-testid="trade-matches-list">
            {data.matches.map((m) => {
              const dl = worthDelta(w, m.worth);
              const done = proposed.includes(m.id);
              return (
                <div key={m.id} className="rounded-xl border border-border bg-card p-3 flex gap-3" data-testid={`trade-match-${m.id}`}>
                  <img src={publicItemImage(m)} alt={m.name} className="h-28 w-20 rounded-lg object-cover bg-secondary shrink-0" />
                  <div className="min-w-0 flex-1 flex flex-col">
                    <p className="text-sm font-semibold truncate">{m.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{m.brand || m.category}{m.size ? ` · ${m.size}` : ""}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-mono-plex font-semibold">{money(m.worth)}</span>
                      {dl && <span className={`text-[11px] font-mono-plex ${Math.abs(dl.pct) <= 5 ? "text-muted-foreground" : dl.d > 0 ? "text-[hsl(var(--chart-2))]" : "text-action"}`}>{dl.d >= 0 ? "+" : ""}{dl.pct}%</span>}
                      <FitBadge fits={m.fits_me} />
                    </div>
                    <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                      <UserChip user={m.user} size="sm" />
                      <Button size="sm" onClick={() => propose(m)} disabled={busy === m.id || done} className="rounded-full shrink-0" data-testid={`propose-trade-${m.id}`}>
                        {busy === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : done ? <><Check className="h-4 w-4 mr-1" /> Sent</> : <><ArrowLeftRight className="h-4 w-4 mr-1" /> Propose trade</>}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>}
      </DialogContent>
    </Dialog>
  );
};

/** My items with quick listing toggles, bulk actions and trade-match counts (lives on my Profile). */
export const ManageListings = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [matchFor, setMatchFor] = useState(null);
  const load = () => api.myListings().then(setData).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);

  const items = useMemo(() => {
    const all = data?.items || [];
    if (filter === "listed") return all.filter((i) => ["sell", "swap", "both"].includes(i.listing_type));
    if (filter === "unlisted") return all.filter((i) => !i.listing_type || i.listing_type === "none");
    if (filter === "sold") return all.filter((i) => i.listing_type === "sold");
    return all;
  }, [data, filter]);

  const setType = async (item, type) => {
    try {
      const price = type === "sell" || type === "both" ? (item.listing_price ?? (item.worth != null ? Math.round(item.worth) : null)) : null;
      await api.updateItem(item.id, { listing_type: type, listing_price: price, listing_note: item.listing_note || null });
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === item.id ? { ...x, listing_type: type, listing_price: price } : x)) }));
      toast.success(type === "none" ? "Removed from the market" : `Listed: ${LISTING_LABEL[type]}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  const setPrice = async (item, price) => {
    const v = Number(price) || null;
    try { await api.updateItem(item.id, { listing_type: item.listing_type, listing_price: v, listing_note: item.listing_note || null }); setData((d) => ({ ...d, items: d.items.map((x) => (x.id === item.id ? { ...x, listing_price: v } : x)) })); }
    catch { toast.error("Could not save price"); }
  };
  const bulk = async (type) => {
    setBusy(true);
    try { await api.setListingType(selected, type); toast.success(`${selected.length} piece${selected.length === 1 ? "" : "s"} → ${LISTING_LABEL[type]}`); setSelected([]); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  if (data === null) return <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-72 rounded-[var(--radius)]" />)}</div>;
  const listedCount = data.listed_count || 0;

  return (
    <div data-testid="manage-listings">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full bg-secondary p-0.5 text-sm">
            {[["all", "All"], ["listed", `Listed (${listedCount})`], ["unlisted", "Not listed"], ["sold", "Sold"]].map(([k, l]) => <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full ${filter === k ? "bg-foreground text-background" : ""}`} data-testid={`listings-filter-${k}`}>{l}</button>)}
          </div>
          <p className="text-xs text-muted-foreground" data-testid="listings-summary">{listedCount} listed · worth {money(data.listed_worth)} · {data.market_swappable} swappable pieces on the market</p>
        </div>
        {selected.length > 0 && (
          <div className="flex items-center gap-2 rounded-full bg-foreground text-background pl-3 pr-1 py-1 text-xs" data-testid="listings-bulk-bar">
            <span>{selected.length} selected</span>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => bulk("swap")} className="rounded-full h-7 text-xs" data-testid="bulk-swap">Open to swap</Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => bulk("both")} className="rounded-full h-7 text-xs" data-testid="bulk-both">Sale or swap</Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => bulk("none")} className="rounded-full h-7 text-xs" data-testid="bulk-none">Remove</Button>
            <button onClick={() => setSelected([])} className="p-1.5 rounded-full hover:bg-background/20" aria-label="Clear selection"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
      {items.length === 0 ? <div className="rounded-[var(--radius)] border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="listings-empty">{filter === "listed" ? "Nothing listed yet — pick pieces below and mark them for sale or swap." : "No pieces here."}</div>
        : <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="listings-grid">
          {items.map((it) => {
            const listed = ["sell", "swap", "both"].includes(it.listing_type);
            const sel = selected.includes(it.id);
            return (
              <div key={it.id} className={`rounded-[var(--radius)] border bg-card overflow-hidden flex flex-col ${sel ? "border-action" : "border-border"}`} data-testid={`listing-card-${it.id}`}>
                <div className="relative aspect-[3/4] bg-secondary">
                  <img src={itemImage(it)} alt={it.name} className="h-full w-full object-cover" loading="lazy" />
                  <label className="absolute top-2 left-2 flex items-center justify-center h-6 w-6 rounded-md bg-background/90"><Checkbox checked={sel} onCheckedChange={() => toggleSel(it.id)} data-testid={`listing-select-${it.id}`} /></label>
                  {listed && <span className="absolute top-2 right-2 rounded-full bg-action text-action-foreground px-2 py-0.5 text-[10px] font-medium">{it.listing_type === "swap" ? "swap" : it.listing_type === "both" ? "sale · swap" : "sale"}</span>}
                  {it.listing_type === "sold" && <span className="absolute top-2 right-2 rounded-full bg-foreground text-background px-2 py-0.5 text-[10px] font-medium">sold</span>}
                  {it.worth != null && <span className="absolute bottom-2 right-2 rounded-full bg-background/90 px-2 py-0.5 text-[11px] font-semibold font-mono-plex">{money(it.worth)}</span>}
                </div>
                <div className="p-3 space-y-2 flex-1 flex flex-col">
                  <div><p className="text-sm font-semibold truncate">{it.name}</p><p className="text-xs text-muted-foreground truncate">{it.brand || it.category}{it.size ? ` · ${it.size}` : ""}</p></div>
                  <Select value={it.listing_type && it.listing_type !== "sold" ? it.listing_type : it.listing_type === "sold" ? "sold" : "none"} onValueChange={(v) => setType(it, v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`listing-type-${it.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not listed</SelectItem>
                      <SelectItem value="sell">For sale</SelectItem>
                      <SelectItem value="swap">Open to swap</SelectItem>
                      <SelectItem value="both">Sale or swap</SelectItem>
                      <SelectItem value="sold">Sold / traded</SelectItem>
                    </SelectContent>
                  </Select>
                  {(it.listing_type === "sell" || it.listing_type === "both") && (
                    <div className="relative"><span className="absolute left-2.5 top-1.5 text-xs text-muted-foreground">$</span><Input type="number" defaultValue={it.listing_price ?? ""} onBlur={(e) => Number(e.target.value) !== Number(it.listing_price) && setPrice(it, e.target.value)} className="h-8 pl-5 text-xs font-mono-plex" placeholder="Asking price" data-testid={`listing-price-${it.id}`} /></div>
                  )}
                  {(it.listing_type === "swap" || it.listing_type === "both") && (
                    <Button size="sm" variant={it.trade_match_count ? "default" : "secondary"} onClick={() => setMatchFor(it)} className="rounded-full h-8 text-xs mt-auto" data-testid={`listing-matches-${it.id}`}>
                      <Scale className="h-3.5 w-3.5 mr-1.5" /> {it.trade_match_count ? `${it.trade_match_count} trade match${it.trade_match_count === 1 ? "" : "es"}` : "Find trades"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>}
      <TradeMatchesDialog item={matchFor} open={!!matchFor} onOpenChange={(o) => !o && setMatchFor(null)} onProposed={onChanged} />
    </div>
  );
};

/** Chat thread attached to one offer, with counter-price and accept inside the thread. */
export const OfferThread = ({ offer: initial, onChanged, defaultOpen = false }) => {
  const [offer, setOffer] = useState(initial);
  const [open, setOpen] = useState(defaultOpen);
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [counter, setCounter] = useState("");
  const [showCounter, setShowCounter] = useState(false);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { setOffer(initial); }, [initial]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [messages?.length, open]);
  useEffect(() => { if (defaultOpen && messages === null) load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try { const r = await api.offerMessages(offer.id); setMessages(r.messages); setOffer(r.offer); onChanged?.("read"); }
    catch { setMessages([]); }
  };
  const toggle = () => { setOpen((v) => !v); if (!open && messages === null) load(); };
  const send = async (payload) => {
    setBusy(true);
    try {
      const r = await api.sendOfferMessage(offer.id, payload);
      setMessages((m) => [...(m || []), r.message]); setOffer(r.offer); setText(""); setCounter(""); setShowCounter(false);
      if (payload.counter_price != null) { toast.success(`Countered at ${money(payload.counter_price)}`); onChanged?.("counter"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  const other = offer.incoming ? offer.from_user : offer.to_user;
  return (
    <div className="mt-3 border-t border-border pt-2" data-testid={`offer-thread-${offer.id}`}>
      <button onClick={toggle} className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground py-1" data-testid={`offer-thread-toggle-${offer.id}`}>
        <span className="inline-flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" /> {offer.message_count ? `${offer.message_count} message${offer.message_count === 1 ? "" : "s"}` : "Message"} with @{other?.handle}
          {offer.unread && <span className="h-2 w-2 rounded-full bg-action" data-testid={`offer-unread-dot-${offer.id}`} />}
          {offer.last_message && !open && <span className="truncate max-w-[200px] text-muted-foreground/80">— {offer.last_message}</span>}
        </span>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid={`offer-thread-panel-${offer.id}`}>
          <div className="max-h-64 overflow-y-auto space-y-1.5 rounded-xl bg-secondary/50 p-2">
            {messages === null ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground m-2" />
              : messages.length === 0 ? <p className="text-xs text-muted-foreground p-2">Start the conversation — ask about condition, fit, shipping, or counter the price.</p>
              : messages.map((m) => (
                m.kind === "system" ? <p key={m.id} className="text-center text-[11px] text-muted-foreground py-1" data-testid={`msg-${m.id}`}>{m.text}</p>
                  : <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm ${m.kind === "counter" ? "border border-wait bg-card" : m.mine ? "bg-foreground text-background" : "bg-card border border-border"}`}>
                      {m.kind === "counter" && <p className="text-[10px] uppercase tracking-wide text-wait font-semibold">Counter · {money(m.counter_price)}</p>}
                      <p>{m.text}</p>
                      <p className={`text-[10px] mt-0.5 ${m.mine && m.kind !== "counter" ? "text-background/70" : "text-muted-foreground"}`}>{m.user?.handle ? `@${m.user.handle} · ` : ""}{timeAgo(m.created_at)}</p>
                    </div>
                  </div>
              ))}
            <div ref={endRef} />
          </div>
          {offer.status === "pending" && offer.kind === "buy" && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">Current price <b className="font-mono-plex text-foreground">{money(offer.price)}</b>{offer.counter_by ? (offer.can_accept ? " (their counter — accept above)" : " (your counter)") : ""}</span>
              {offer.can_counter && <Button size="sm" variant="outline" onClick={() => setShowCounter((v) => !v)} disabled={busy} className="rounded-full h-7 text-xs" data-testid={`thread-counter-toggle-${offer.id}`}><Tag className="h-3.5 w-3.5 mr-1" /> Counter</Button>}
            </div>
          )}
          {showCounter && (
            <div className="flex gap-2 items-center rounded-xl border border-wait/50 p-2">
              <span className="text-xs text-muted-foreground shrink-0">Counter at</span>
              <div className="relative flex-1"><span className="absolute left-2.5 top-2 text-xs text-muted-foreground">$</span><Input type="number" value={counter} onChange={(e) => setCounter(e.target.value)} placeholder={String(Math.round(offer.price || 0))} className="h-8 pl-5 font-mono-plex" data-testid={`thread-counter-input-${offer.id}`} /></div>
              <Button size="sm" onClick={() => send({ counter_price: Number(counter), text: text.trim() || null })} disabled={busy || !Number(counter)} className="rounded-full h-8" data-testid={`thread-counter-send-${offer.id}`}>Send counter</Button>
            </div>
          )}
          <div className="flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && text.trim() && send({ text })} placeholder={offer.status === "pending" ? "Message…" : "Chat stays open after the deal — arrange shipping here"} className="h-9" data-testid={`thread-input-${offer.id}`} />
            <Button size="sm" onClick={() => send({ text })} disabled={busy || !text.trim()} className="rounded-full h-9" data-testid={`thread-send-${offer.id}`}><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Small coral-sparkle header used above lists. */
export const TradeHint = ({ children }) => (
  <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-action" /> {children}</p>
);
